import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, CreditCard, Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";

import Logo from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { request, submitPayFastPaymentForm } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { readRenewalEntry, renewalNeedsPolling, scrubRenewalTokenFromUrl, submitRenewalCheckout } from "@/lib/renewal-payment";

const ACTIVE_STATES = new Set(["paid", "already_active", "covered"]);

export default function Pay() {
  const location = useLocation();
  const entry = useMemo(() => readRenewalEntry(), [location.key]);
  const { tx } = useI18n();
  const [payload, setPayload] = useState({ state: entry.token ? "loading" : "invalid" });
  const [attempts, setAttempts] = useState(0);
  const [checking, setChecking] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(false);

  const load = useCallback(async (checkout = false, confirmPriceChange = false, confirmedPrice = undefined) => {
    if (!entry.token || inFlight.current) return;
    inFlight.current = true;
    setChecking(true);
    try {
      const result = await request(`/api/renewal/${checkout ? "checkout" : "status"}`, {
        method: "POST",
        body: JSON.stringify({ token: entry.token, ...(checkout ? { confirmPriceChange, ...(confirmPriceChange ? { confirmedPrice } : {}) } : {}) }),
        requireAuth: false,
        cache: "no-store",
        referrerPolicy: "no-referrer",
      });
      if (!mounted.current) return;
      if (!result || typeof result.state !== "string") throw new Error("Invalid payment response");
      setPayload(result);
      if (checkout) submitRenewalCheckout(result, submitPayFastPaymentForm);
    } catch (error) {
      if (!mounted.current) return;
      setPayload({ state: error?.status === 410 ? "expired" : [400, 409].includes(error?.status) ? "invalid" : "error" });
    } finally {
      inFlight.current = false;
      if (mounted.current) setChecking(false);
    }
  }, [entry.token]);

  useEffect(() => {
    mounted.current = true;
    scrubRenewalTokenFromUrl();
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    void load(!entry.outcome);
    return () => { mounted.current = false; meta.remove(); };
  }, [entry.outcome, load]);

  useEffect(() => {
    if (!renewalNeedsPolling(payload.state, attempts, payload.reason)) return undefined;
    const timer = window.setTimeout(() => {
      setAttempts((value) => value + 1);
      void load();
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [attempts, load, payload]);

  const active = ACTIVE_STATES.has(payload.state);
  const confirmingPrice = payload.state === "requires_confirmation";
  const opening = ["loading", "checkout"].includes(payload.state);
  const unavailable = ["invalid", "expired", "credit_review"].includes(payload.state);
  const needsSupport = payload.reason === "existing_checkout_needs_review";
  const cancelled = !needsSupport && (payload.state === "cancelled" || (entry.outcome === "cancelled" && !active && !confirmingPrice && !opening && !unavailable && payload.state !== "error"));
  const canCheckout = confirmingPrice || ["ready", "failed", "cancelled"].includes(payload.state) || cancelled;
  const amount = Number.isFinite(payload.amountCents) ? `R${(payload.amountCents / 100).toFixed(2)}` : "";
  const title = needsSupport ? "Message Min Support" : opening ? "Opening PayFast"
    : active ? "Your Fun access is active."
      : confirmingPrice ? "The price has changed."
        : unavailable ? "Payment link unavailable"
          : cancelled ? "Checkout cancelled"
            : payload.state === "failed" ? "Payment failed"
              : payload.state === "ready" ? "Continue to PayFast"
                : payload.state === "error" ? "Please try again."
                  : "Confirming payment";

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background ph-no-capture">
      <nav className="px-5 py-4"><Logo size="sm" /></nav>
      <main className="flex flex-1 items-center justify-center px-5 pb-16">
        <section aria-live="polite" className="w-full max-w-sm rounded-3xl border border-border bg-card p-6 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            {active ? <CheckCircle2 aria-hidden="true" className="h-7 w-7" /> : opening || (payload.state === "pending" && !cancelled && !needsSupport) ? <Loader2 aria-hidden="true" className="h-7 w-7 animate-spin" /> : confirmingPrice || canCheckout ? <CreditCard aria-hidden="true" className="h-7 w-7" /> : <AlertCircle aria-hidden="true" className="h-7 w-7" />}
          </div>
          <h1 className="font-display text-2xl font-bold">{tx(title)}</h1>
          {active ? <p className="mt-3 text-sm text-muted-foreground">{tx(payload.state === "paid" ? "Payment confirmed" : "No payment needed.")}</p> : null}
          {unavailable || needsSupport ? <p className="mt-3 text-sm text-muted-foreground">{tx("Message Min Support and we will send a fresh link.")}</p> : null}
          {payload.state === "pending" && !cancelled && !needsSupport ? <p className="mt-3 text-sm text-muted-foreground">{tx("We are waiting for PayFast. You can reopen this link to check again.")}</p> : null}
          {cancelled || payload.state === "failed" ? <p className="mt-3 text-sm text-muted-foreground">{tx("PayFast did not confirm the payment. Try again when you’re ready.")}</p> : null}
          {canCheckout && amount ? (
            <div className="mt-5 rounded-2xl bg-muted/50 px-4 py-4">
              <p className="text-sm font-semibold">Fun</p>
              <p className="mt-1 text-3xl font-bold">{amount}<span className="text-sm font-normal text-muted-foreground">{tx("/month")}</span></p>
              <p className="mt-2 text-sm text-muted-foreground">{tx("Billed now and every month. Cancel anytime.")}</p>
              {payload.discountCycles === 6 ? <p className="mt-2 text-sm text-muted-foreground">{tx("Discount applies to 6 payments.")}</p> : null}
            </div>
          ) : null}
          {canCheckout ? <Button className="mt-5 h-12 w-full rounded-xl" disabled={checking} onClick={() => void load(true, confirmingPrice, { amountCents: payload.amountCents, discountCycles: payload.discountCycles, standardAmountCents: payload.standardAmountCents })}>{checking ? <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" /> : null}{tx("Continue to PayFast")}</Button> : null}
          {(payload.state === "pending" && attempts >= 40 && !needsSupport) || payload.state === "error" ? <Button variant="outline" className="mt-5 h-12 w-full rounded-xl" disabled={checking} onClick={() => { setAttempts(0); void load(); }}>{tx("Check again")}</Button> : null}
          {active && payload.needsRelink ? <p className="mt-4 text-sm text-muted-foreground">{tx("Your WhatsApp still needs relinking. Reply to Min Support for help.")}</p> : null}
          {unavailable || needsSupport || (active && payload.needsRelink) || payload.state === "error" ? <Button asChild variant="outline" className="mt-5 h-12 w-full rounded-xl"><a href="https://wa.me/27686044011" rel="noreferrer">{tx("Message Min Support")}</a></Button> : null}
          {active && !payload.needsRelink ? <p className="mt-4 text-sm text-muted-foreground">{tx("You can close this page.")}</p> : null}
        </section>
      </main>
    </div>
  );
}
